import { GameSource } from "./GameSource.js";
import type { MoveGameEvent } from "../../event/MoveGameEvent.js";
import type { RollGameEvent } from "../../event/RollGameEvent.js";
import { PlayerType } from "../../../../royalur/model/PlayerType.js";
import type { Game } from "../../../../royalur/Game.js";
import { getTimeMs } from "../../../../util/utils.js";
import { GameMode } from "../../GameMode.js";
import { GameUpdateEvent } from "./GameUpdateEvent.js";
import { GamePlayers } from "../../../api/game/GamePlayers.js";
import { GamePlayerData } from "../../../api/game/GamePlayerData.js";
import { BotType } from "../../BotType.js";
import type { AnalyticsProvider } from "../../../analytics/Analytics.js";
import type { MessageOutUploadGame } from "../../../api/game/MessageOutUploadGame.js";
import { BotLobbyPlayer } from "../../../lobby/BotLobbyPlayer.js";
import type { GameAPI } from "../../../api/GameAPI.js";
import type { APIGamePreferences } from "../../../api/api_schema.js";
import type { GameClientControls } from "../../../GameClientControls.js";
import type { ReactionType } from "../../ReactionType.js";
import { LocalLobbyStatus } from "../status/LocalLobbyStatus.js";
import type { GameController } from "../GameController.js";
import { KnownLobbySettings } from "../../KnownLobbySettings.js";
import { Ruleset } from "../../Ruleset.js";
import { SimpleEngineProvider } from "../../../../royalur/engine/simple/SimpleEngineProvider.js";
import { ClientError } from "../../error/ClientError.js";
import { Optional } from "../../../../util/Optional.js";
import { LobbyID } from "../../../lobby/LobbyID.js";
import { ErrorType } from "../../error/ErrorType.js";
import { Rune } from "../../../Rune.js";


export type PerfectGameSourceMode = "idle" | "load" | "play";


export class PerfectGameSource extends GameSource {
    public static readonly CHECK_INTERVAL_MS = 250;

    public static readonly SETTINGS = new KnownLobbySettings(
        GameMode.COMPUTER,
        Ruleset.FINKEL,
        Ruleset.FINKEL.getSettings(),
        BotType.PANDA,
    );

    public static readonly RULES = new SimpleEngineProvider().create(
        PerfectGameSource.SETTINGS.getGameSettings(),
    );

    public readonly currentGame: Rune<Optional<Game>>;
    public readonly mode: Rune<PerfectGameSourceMode>;

    private currentGamePromise: Promise<void> | null = null;
    private lastGameFetchMs: number = 0;
    private failedGameFetches: number = 0;

    private gameController: GameController | null = null;
    private lastMonitorMs: number;

    constructor(
        initialClientControls: GameClientControls,
        initialPreferences: APIGamePreferences,
        analytics: AnalyticsProvider,
        gameAPI: GameAPI,
    ) {
        super(
            initialClientControls, initialPreferences, analytics, gameAPI, PerfectGameSource.SETTINGS,
            new LocalLobbyStatus(
                LobbyID.generateRandom(),
                GameMode.COMPUTER,
                PerfectGameSource.SETTINGS.getBotType(),
                null, null, null,
            ),
            PerfectGameSource.RULES,
        );

        this.currentGame = new Rune<Optional<Game>>(Optional.empty());
        this.mode = new Rune<PerfectGameSourceMode>("idle");
        this.lastMonitorMs = getTimeMs();
        this.handleGameSetup();
    }

    override getControllerMaxDirectives(): number {
        // A value of 0 represents no limit.
        return 0;
    }

    override isLocalHumanPlayer(_playerType: PlayerType): boolean {
        return false;
    }

    loadNewGame() {
        this.mode.set("load");
    }

    private handleGameSetup(): void {
        const game = this.getRules().generateGame();

        const lightData = new GamePlayerData(1, PlayerType.LIGHT);
        const darkData = new GamePlayerData(2, PlayerType.DARK);

        const botType = PerfectGameSource.SETTINGS.getBotType();
        const lightPlayer = new BotLobbyPlayer(botType, lightData);
        const darkPlayer = new BotLobbyPlayer(botType, darkData);

        this.updateGameAndPlayers(
            new GamePlayers(
                PlayerType.LIGHT,
                null,
                lightPlayer,
                darkPlayer,
            ),
            game, new GameUpdateEvent(true),
        );
        this.lobbyStatus.map(status => status.withCompleteGameID(null));
    }

    override setup(): () => void {
        const interval = setInterval(() => {
            this.monitor();
        }, PerfectGameSource.CHECK_INTERVAL_MS + 10);

        return () => {
            clearInterval(interval);
        };
    }

    linkGameController(gameController: GameController): () => void {
        this.gameController = gameController;
        return () => {
            this.gameController = null;
        };
    }

    monitor() {
        const timeSinceMonitorMs = getTimeMs() - this.lastMonitorMs;
        if (timeSinceMonitorMs < PerfectGameSource.CHECK_INTERVAL_MS)
            return;

        this.maybeDownloadNewGame();
        this.lastMonitorMs = getTimeMs();
    }

    private maybeDownloadNewGame() {
        if (this.currentGamePromise !== null)
            return;
        if (this.gameController === null)
            return;

        const game = this.game.get();
        const currentGame = this.currentGame.get().getOrNull();
        if (game.isFinished() || currentGame === null) {
            if (this.mode.get() === "load") {
                this.downloadNewGame();
            }
        } else if (game !== currentGame) {
            this.game.set(currentGame, new GameUpdateEvent(false));
        }
    }

    override handleResign(): void {
        throw new Error("Unimplemented for replay games");
    }

    override handleMove(_event: MoveGameEvent): void {
        throw new Error("Unimplemented for replay games");
    }

    override handleRoll(_event: RollGameEvent): void {
        throw new Error("Unimplemented for replay games");
    }

    override handleReaction(_player: PlayerType | null, _reaction: ReactionType) {
        throw new Error("Unimplemented for replay games");
    }

    private downloadNewGame() {
        // If we are already loading the game, ignore this.
        if (this.currentGamePromise !== null)
            return;

        // Wait until this game finishes being animated.
        if (!this.gameController || this.gameController.getQueuedDirectiveCount() > 0)
            return;

        // Exponential backoff.
        if (this.failedGameFetches > 0) {
            const timeSinceFail = getTimeMs() - this.lastGameFetchMs;
            const timeMul = Math.pow(2, Math.min(5, this.failedGameFetches));
            const timeToWait = PerfectGameSource.CHECK_INTERVAL_MS * timeMul;
            if (timeSinceFail < timeToWait)
                return;
        }

        // Attempt to fetch a game to display.
        this.lastGameFetchMs = getTimeMs();
        this.currentGamePromise = this.getGameAPI().getPandaGame()
            .then((pandaGame) => {
                this.currentGame.set(Optional.of(pandaGame));
                this.mode.set("play");
                this.handleGameSetup();
                this.failedGameFetches = 0;
                this.error.set(Optional.empty());
            })
            .catch((err) => {
                if (err instanceof ClientError) {
                    this.error.set(Optional.item(err));
                } else {
                    const errorMessage = (
                        err instanceof Error ? err.message
                            : (typeof err === "string" ? err
                                : "Unknown error"));

                    this.error.set(Optional.item(new ClientError(ErrorType.UNKNOWN, errorMessage)));
                }
                this.failedGameFetches += 1;
            })
            .finally(() => {
                this.currentGamePromise = null;
            });
    }

    override shouldUploadGame(): boolean {
        return false;
    }

    override buildUploadGameMessage(): MessageOutUploadGame {
        throw new Error("Unimplemented for self-play games");
    }
}
